import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Header behaviour: the mobile drawer, and keeping the cart badge honest.
 *
 * The drawer is a native <dialog>, so focus trapping, Escape-to-close and an inert
 * background come from the platform. Only open/close needs wiring.
 *
 * The badge is rendered by Liquid at page render, which Shopify caches, and Horizon
 * updates the cart without a page load — so the number would drift. This listens for the
 * same `StandardEvents.cartLinesUpdate` that Horizon's own cart icon uses, plus the
 * `cart:refresh` event the custom sections dispatch, and re-reads /cart.js.
 *
 * NOTE: the handler is a private FIELD holding an arrow function, not a private method.
 * Private methods are non-writable, so `this.#handler = this.#handler.bind(this)` throws
 * a TypeError — which previously killed connectedCallback and left the badge frozen.
 */
class EbHeader extends Component {
  requiredRefs = ['drawer'];

  #events = [StandardEvents?.cartLinesUpdate, 'cart:refresh', 'cart:update'].filter(Boolean);

  /** Arrow function in a field: already bound, and assignable. */
  #syncCount = async () => {
    const badge = this.querySelector('[data-eb-cart-count]');
    if (!badge) return;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();

      badge.textContent = String(cart.item_count);
      badge.toggleAttribute('data-empty', cart.item_count === 0);

      // Brief pulse so the change is noticed rather than silently swapping.
      badge.classList.remove('is-bumped');
      void badge.offsetWidth;
      badge.classList.add('is-bumped');
    } catch {
      // A failed refresh must never break the header. Keep the rendered value.
    }
  };

  connectedCallback() {
    super.connectedCallback();
    for (const name of this.#events) document.addEventListener(name, this.#syncCount);
    // Bring the badge in line with the real cart on load, in case the page came from cache.
    this.#syncCount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    for (const name of this.#events) document.removeEventListener(name, this.#syncCount);
  }

  openDrawer(event) {
    this.refs.drawer.showModal();
    event?.currentTarget?.setAttribute('aria-expanded', 'true');
  }

  closeDrawer() {
    this.refs.drawer.close();
    this.querySelector('.eb-header__burger')?.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('eb-header')) {
  customElements.define('eb-header', EbHeader);
}
